/**
 * ShareWallPill — organiser-only "Share this wall ✦" affordance on
 * the Memory Wall. Closes the viral loop Craig flagged: every host
 * becomes an organic distribution channel, and every shared link is
 * a real preserved night (not advertising).
 *
 * Restraints (matching the rest of the wall's pill family):
 *   • organiser-only — host detection via useAuth + event.user_id
 *   • mutually exclusive with the keepsake + chapter pills — passed
 *     `hidden` when either of those would surface
 *   • glass-dark + gold-border italic-serif, NEVER a bright CTA
 *   • copy verbatim: "Share this wall ✦"
 *   • tap opens a soft modal with:
 *       - the share URL (selectable, copy-on-click)
 *       - native share API on mobile (`navigator.share`) when present
 *       - a quiet "preview the share" link → /api/share/{slug}
 *       - a soft serif paragraph framing what gets shared
 *   • whisper-style brandToast confirmation, no loud "✓ Copied!" CTA
 *
 * The share URL format is intentional: `{origin}/share/{slug}`. That
 * route serves OG/Twitter meta tags + a 0ms redirect to `/?demo={slug}`,
 * so social platforms render the wall preview while humans land on the
 * cinematic homepage hero.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { brandToast } from '../lib/brandToast';

export default function ShareWallPill({ slug, isHost, hidden = false }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // The share URL is the OG-aware backend route. Falls back to the
  // raw demo URL if the host is on a non-standard origin (preview etc).
  const shareUrl = useMemo(() => {
    if (!slug) return '';
    const origin = window.location.origin.replace(/\/$/, '');
    // Prefer the OG-aware backend route — it handles meta tags AND
    // redirects to /?demo={slug} for humans. Use a relative path that
    // resolves against the public origin.
    return `${origin}/api/share/${encodeURIComponent(slug)}`;
  }, [slug]);

  useEffect(() => { if (!open) setCopied(false); }, [open]);

  if (!isHost || hidden || !slug) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      brandToast.whisper('The wall is yours to share ✦');
      setTimeout(() => setCopied(false), 2400);
    } catch {
      brandToast.slipped('The link slipped away · please try again');
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) { handleCopy(); return; }
    try {
      await navigator.share({
        title: 'A preserved night ✦',
        text: 'Wander through this Memory Wall ✦',
        url: shareUrl,
      });
    } catch {
      // user cancelled — silent, no toast
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="memory-wall-share-pill"
        aria-label="Share this wall"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '10px 18px',
          borderRadius: 999,
          background: 'rgba(20,12,6,0.62)',
          border: '1px solid rgba(212,170,82,0.30)',
          backdropFilter: 'blur(12px)',
          color: 'rgba(255,235,200,0.85)',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: 12.5,
          letterSpacing: '0.01em',
          cursor: 'pointer',
          boxShadow: '0 8px 22px rgba(0,0,0,0.4)',
          transition: 'transform 320ms ease, color 320ms ease, border-color 320ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#FFE7A8';
          e.currentTarget.style.borderColor = 'rgba(212,170,82,0.55)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(255,235,200,0.85)';
          e.currentTarget.style.borderColor = 'rgba(212,170,82,0.30)';
          e.currentTarget.style.transform = 'none';
        }}
      >
        <span aria-hidden style={{ fontSize: 13, color: '#D4AF5E' }}>✦</span>
        Share this wall
      </button>

      {open && (
        <div
          data-testid="share-wall-modal"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(6,3,1,0.84)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(560px, 100%)',
              background: '#1a1108',
              border: '1px solid rgba(212,170,82,0.30)',
              borderRadius: 16,
              padding: '28px 28px 24px',
              boxShadow: '0 36px 96px rgba(0,0,0,0.6)',
              color: '#FBEED0',
            }}
          >
            <div style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(212,170,82,0.82)', marginBottom: 6,
            }}>
              Share the night
            </div>
            <h3 style={{
              margin: 0, marginBottom: 12,
              fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 500,
              color: '#FFE7A8', letterSpacing: '-0.005em',
            }}>
              Send this wall to those who came ✦
            </h3>
            <p style={{
              margin: 0, marginBottom: 22,
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: 14, lineHeight: 1.55,
              color: 'rgba(255,235,200,0.66)',
            }}>
              The link opens the EventDrop homepage with your wall preserved at the top — a real night, with the real memories your guests left behind.
            </p>

            <div style={{
              display: 'flex', alignItems: 'stretch', gap: 0,
              border: '1px solid rgba(212,170,82,0.28)',
              borderRadius: 10,
              background: 'rgba(20,12,6,0.65)',
              overflow: 'hidden',
              marginBottom: 18,
            }}>
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.currentTarget.select()}
                data-testid="share-wall-url"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  padding: '14px 16px',
                  color: '#FFE7A8',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontStyle: 'italic',
                  fontSize: 14,
                  letterSpacing: '0.01em',
                }}
              />
              <button
                type="button"
                onClick={handleCopy}
                data-testid="share-wall-copy-btn"
                style={{
                  background: 'rgba(40,28,14,0.7)',
                  border: 'none',
                  borderLeft: '1px solid rgba(212,170,82,0.20)',
                  color: copied ? '#FFE7A8' : '#D4AF5E',
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: 13,
                  padding: '0 18px',
                  cursor: 'pointer',
                  letterSpacing: '0.04em',
                }}
              >
                {copied ? 'Copied ✦' : 'Copy'}
              </button>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 14, flexWrap: 'wrap',
            }}>
              <a
                href={shareUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="share-wall-preview-link"
                style={{
                  fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  fontSize: 13, color: 'rgba(255,235,200,0.62)',
                  textDecoration: 'underline',
                  textDecorationColor: 'rgba(212,170,82,0.22)',
                  textUnderlineOffset: 4,
                }}
              >
                Preview how it looks shared →
              </a>
              {typeof navigator !== 'undefined' && navigator.share && (
                <button
                  type="button"
                  onClick={handleNativeShare}
                  data-testid="share-wall-native-btn"
                  style={{
                    height: 40, padding: '0 18px', borderRadius: 999,
                    background: 'rgba(18,12,6,0.65)',
                    border: '1px solid rgba(212,170,82,0.45)',
                    backdropFilter: 'blur(10px)',
                    color: '#FFE7A8',
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic',
                    fontSize: 13,
                    letterSpacing: '0.01em',
                    cursor: 'pointer',
                  }}
                >
                  Share ✦
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="share-wall-close-btn"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,235,200,0.42)',
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
